import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Alert, App, Col, Row } from 'antd';
import { Flex, MediaCapture } from '@/components';
import { useFaceEnrollmentPermission, useFaceErrorFn } from '@/hooks';
import { useCallback, useEffect, useState } from 'react';
import { useUniversityContext, useUserContext } from '@/context';
import { AxiosResponse } from 'axios';
import { DataMoonHttp, UserHttp } from '@/services';
import { convertFileToBase64 } from '@/utils';
import { Routes } from '@/routes';

export function FaceEnrollment() {

  const [t] = useTranslation(['common', 'face']);
  const navigate = useNavigate();
  const { message } = App.useApp();
  const parseFaceError = useFaceErrorFn();
  const { profile, refresh: refreshProfile } = useUserContext();
  const { university } = useUniversityContext();
  const [loading, setLoading] = useState(false);
  const { error, setError, requestCamera, stopStream } = useFaceEnrollmentPermission();
  const samad = university?.samad;

  const handleUpload = useCallback(async (file: File) => {
    try {
      if (!['video', 'image'].includes(samad.faceEnrollmentType)) {
        message.error(t('face:errors.invalid-auth-type'));
        return;
      }

      let response: AxiosResponse;
      let imageBase64: string;
      setLoading(true);
      if (samad.faceEnrollmentType === 'video') {
        response = await DataMoonHttp.enrollVideo(profile?.username, file);
      }
      else {
        response = await DataMoonHttp.enroll(profile?.username, file);
        imageBase64 = await convertFileToBase64(file as any);
        if (response?.data?.success) {
          await UserHttp.uploadProfilePicture({
            username: profile?.username,
            userImageBase64: imageBase64,
          });
        }
        else {
          message.error(parseFaceError());
          return;
        }
      }
      message.success(t('face:messages.success-face-enrollment'));
      setTimeout(() => navigate(Routes.Home), 1000);
      setTimeout(() => refreshProfile(), 1200);
    }
    catch (e) {
      message.error({ duration: 4.5, content: parseFaceError(e?.response?.data) });
    }
    finally {
      setLoading(false);
    }
  }, [message, navigate, profile?.username, refreshProfile, samad.faceEnrollmentType, t, parseFaceError]);

  useEffect(() => {
    if (!samad?.faceEnrollment) {
      navigate({ pathname: Routes.Home }, { replace: true });
    }
  }, []);

  useEffect(() => {
    return () => stopStream();
  }, [stopStream]);

  if (error) {
    return (
      <Row justify="center" className="mx-auto">
        <Col xl={16} lg={20} span={24} className="mx-auto">
          <Alert type="error" message={(
            <Flex direction="row" gap="0">
              <p>{error}</p>
            </Flex>
          )} />
        </Col>
      </Row>
    );
  }

  return (
    <Flex className="w-full h-full max-h-[480px] my-auto mx-auto pb-8 md:pb-0">
      <MediaCapture
        onInit={requestCamera}
        loading={loading}
        onSubmit={handleUpload}
        type={university?.samad?.faceEnrollmentType}
        onError={(e) => setError(e.toString())}
        descriptionClassname="mt-4 !text-justify"
        buttonStartText={t('face:labels.start-authentication')}
        description={t('face:messages.face-enroll-first-hint')}
      />
    </Flex>
  );
}
